import {Component, OnDestroy, OnInit} from '@angular/core';
import {Booking, BookingStatus} from '../../../core/entities/booking/booking.entity';
import {BookingService} from '../../services/booking/booking.service';
import {ToastrService} from 'ngx-toastr';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {CurrencyPipe, DatePipe, NgClass} from '@angular/common';
import {ImageUploadComponent, PreparedBatch} from '../../../core/components/image-upload/image-upload.component';
import {PhotoService} from '../../services/photo/photo.service';
import {ActivatedRoute} from '@angular/router';
import {HttpErrorResponse} from '@angular/common/http';
import {AppImageComponent} from '../../../core/components/image/image';
import {DateTime, Duration} from 'luxon';

@Component({
  selector: 'app-booking',
  imports: [
    TranslocoPipe,
    DatePipe,
    ImageUploadComponent,
    NgClass,
    CurrencyPipe,
    AppImageComponent
  ],
  templateUrl: './booking.component.html',
  styleUrl: './booking.component.scss'
})
export class BookingComponent implements OnInit, OnDestroy {
  public booking: Booking|null = null;
  public statusInProgress: BookingStatus|null = null;
  public balanceRetryUnlockAt: DateTime|null = null;
  public balanceRetryRemainingMs: number|null = null;
  public balanceRetryErrorMessage: string|null = null;
  private balanceRetryTimer: ReturnType<typeof setInterval>|null = null;
  private readonly batchUploadToasts = new Map<number, number>();

  public constructor(
    private readonly bookingService: BookingService,
    private readonly photoService: PhotoService,
    private readonly toastr: ToastrService,
    private readonly transloco: TranslocoService,
    private readonly route: ActivatedRoute,
  ) {
  }

  public ngOnInit() {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    this.bookingService.getBooking(id).subscribe(
      (booking) => {
        this.booking = booking;
        this.syncBalanceRetryState(booking);
      },
      () => {
        this.toastr.error(
          this.transloco.translate('bookings.booking.errors.retrieve.content'),
          this.transloco.translate('bookings.booking.errors.retrieve.title')
        );
      }
    )
  }

  public ngOnDestroy(): void {
    this.stopBalanceRetryTimer();
  }

  public onBatchReady(batch: PreparedBatch): void {
    if (!this.booking || !batch.files.length) {
      return;
    }

    const toastRef = this.toastr.info(
      this.transloco.translate('bookings.booking.photos.toasts.loading.content'),
      this.transloco.translate('bookings.booking.photos.toasts.loading.title'),
      { disableTimeOut: true }
    );
    this.batchUploadToasts.set(batch.batchIndex, toastRef.toastId);

    this.photoService.uploadPhotos(this.booking.id, batch.files).subscribe({
      next: (photos) => {
        this.toastr.clear(toastRef.toastId);
        if (!this.booking) {
          return;
        }
        this.booking.photos = [
          ...(this.booking?.photos ?? []),
          ...photos
        ];
        this.toastr.success(
          this.transloco.translate('bookings.booking.photos.toasts.success.content'),
          this.transloco.translate('bookings.booking.photos.toasts.success.title')
        );
        this.batchUploadToasts.delete(batch.batchIndex);
      },
      error: (error: HttpErrorResponse) => {
        this.toastr.clear(toastRef.toastId);

        let messageKey = 'bookings.booking.photos.toasts.error.content';
        let titleKey = 'bookings.booking.photos.toasts.error.title';

        if (error.status === 400) {
          messageKey = 'bookings.booking.photos.toasts.error.invalidFile.content';
          titleKey = 'bookings.booking.photos.toasts.error.invalidFile.title';
        } else if (error.status === 413) {
          messageKey = 'bookings.booking.photos.toasts.error.tooLarge.content';
          titleKey = 'bookings.booking.photos.toasts.error.tooLarge.title';
        } else if (error.status >= 500) {
          messageKey = 'bookings.booking.photos.toasts.error.server.content';
          titleKey = 'bookings.booking.photos.toasts.error.server.title';
        }

        this.toastr.error(
          this.transloco.translate(messageKey),
          this.transloco.translate(titleKey)
        );
        this.batchUploadToasts.delete(batch.batchIndex);
      }
    });
  }

  public deleteImage(id: number): void {
    const confirmed = window.confirm(
      this.transloco.translate('bookings.booking.photos.delete.confirm')
    );

    if (!confirmed) {
      return; // If user cancels, exit the method
    }

    this.photoService.deletePhoto(id).subscribe(
      () => {
        this.booking!.photos = this.booking!.photos.filter(photo => photo.id !== id);
        this.toastr.success(
          this.transloco.translate('bookings.booking.photos.delete.toasts.success.content'),
          this.transloco.translate('bookings.booking.photos.delete.toasts.success.title')
        );
      }, () => {
        this.toastr.error(
          this.transloco.translate('bookings.booking.photos.delete.toasts.error.content'),
          this.transloco.translate('bookings.booking.photos.delete.toasts.error.title')
        );
      }
    )
  }

  public isAccepted(): boolean {
    return this.booking !== null && [
      BookingStatus.accepted,
      BookingStatus.shooting_done,
      BookingStatus.done
    ].includes(this.booking.status as BookingStatus);
  }

  public canAccept(): boolean {
    return this.booking !== null && this.booking.status === BookingStatus.payed;
  }

  public isShootingDone(): boolean {
    return this.booking !== null && [
      BookingStatus.shooting_done,
      BookingStatus.done
    ].includes(this.booking.status as BookingStatus);
  }

  public canMarkAsShootingDone(): boolean {
    return this.booking !== null
      && this.booking.status === BookingStatus.accepted
      && !this.isShootingBlocked();
  }

  public isDone(): boolean {
    return this.booking !== null && this.booking.status === BookingStatus.done;
  }

  public canMarkAsDone(): boolean {
    return this.booking !== null
      && this.booking.photos.length > 0
      && this.booking.status === BookingStatus.shooting_done;
  }

  public canShowPhotos(): boolean {
    return this.booking !== null
      && (this.booking.status === BookingStatus.shooting_done || this.booking.status === BookingStatus.done);
  }

  public canShowCancelBanner(): boolean {
    return this.booking !== null && this.booking.status !== BookingStatus.cancelled && this.booking.status !== BookingStatus.done;
  }

  public changeBookingStatus(status: BookingStatus): void {
    if (!this.booking) {
      return;
    }

    this.statusInProgress = status;

    if (![
      BookingStatus.pending,
      BookingStatus.payed,
      BookingStatus.accepted,
      BookingStatus.shooting_done,
      BookingStatus.done,
      BookingStatus.cancelled,
    ].includes(status)) {
      this.toastr.error(
        this.transloco.translate('bookings.manage-status.toasts.error.content'),
        this.transloco.translate('bookings.manage-status.toasts.error.title'),
      );
    }

    this.bookingService.changeBookingStatus(this.booking.id, status).subscribe(
      (booking) => {
        this.booking = booking;
        this.syncBalanceRetryState(booking);
        this.toastr.success(
          this.transloco.translate('bookings.manage-status.toasts.success.content'),
          this.transloco.translate('bookings.manage-status.toasts.success.title'),
        );
      }, (error: HttpErrorResponse) => {
        if (status === BookingStatus.shooting_done && error?.error?.code === 'balance_payment_failed') {
          const backendMessage = typeof error?.error?.message === 'string'
            ? error.error.message
            : null;
          this.applyBalanceLockFromError(error);
          this.toastr.error(
            backendMessage ?? this.transloco.translate('bookings.manage-status.toasts.balance-payment-failed.content'),
            this.transloco.translate('bookings.manage-status.toasts.balance-payment-failed.title'),
          );
          return;
        }

        this.toastr.error(
          this.transloco.translate('bookings.manage-status.toasts.error.content'),
          this.transloco.translate('bookings.manage-status.toasts.error.title'),
        );
      }
    ).add(() => {
      this.statusInProgress = null;
    });
  }

  public isShootingBlocked(): boolean {
    return this.balanceRetryUnlockAt !== null;
  }

  public get balanceRetryUnlockDate(): Date|null {
    return this.balanceRetryUnlockAt ? this.balanceRetryUnlockAt.toJSDate() : null;
  }

  public get balanceRetryCountdown(): string|null {
    if (this.balanceRetryRemainingMs === null) {
      return null;
    }

    const duration = Duration.fromMillis(this.balanceRetryRemainingMs).shiftTo('days', 'hours', 'minutes', 'seconds');

    const parts: string[] = [];
    if (duration.days > 0) {
      parts.push(`${Math.floor(duration.days)}d`);
    }
    parts.push(
      `${Math.floor(duration.hours).toString().padStart(2, '0')}:` +
      `${Math.floor(duration.minutes).toString().padStart(2, '0')}:` +
      `${Math.floor(duration.seconds).toString().padStart(2, '0')}`
    );

    return parts.join(' ');
  }

  private syncBalanceRetryState(booking: Booking): void {
    const hasBalanceLock = booking.status === BookingStatus.accepted
      && booking.balanceStatus === 'failed'
      && !!booking.balanceDueAt
      && booking.balanceDueAt.toMillis() > DateTime.now().toMillis();

    if (!hasBalanceLock) {
      this.balanceRetryUnlockAt = null;
      this.balanceRetryRemainingMs = null;
      this.balanceRetryErrorMessage = null;
      this.stopBalanceRetryTimer();
      return;
    }

    this.balanceRetryUnlockAt = booking.balanceDueAt!;
    this.balanceRetryErrorMessage = null;
    this.refreshBalanceRetryRemaining();
    this.startBalanceRetryTimer();
  }

  private refreshBalanceRetryRemaining(): void {
    if (!this.balanceRetryUnlockAt) {
      this.balanceRetryRemainingMs = null;
      return;
    }

    const remainingMs = this.balanceRetryUnlockAt.toMillis() - DateTime.now().toMillis();

    if (remainingMs <= 0) {
      this.balanceRetryUnlockAt = null;
      this.balanceRetryRemainingMs = null;
      this.balanceRetryErrorMessage = null;
      this.stopBalanceRetryTimer();
      return;
    }

    this.balanceRetryRemainingMs = remainingMs;
  }

  private startBalanceRetryTimer(): void {
    if (this.balanceRetryTimer !== null) {
      return;
    }

    this.balanceRetryTimer = setInterval(() => {
      this.refreshBalanceRetryRemaining();
    }, 1000);
  }

  private stopBalanceRetryTimer(): void {
    if (this.balanceRetryTimer !== null) {
      clearInterval(this.balanceRetryTimer);
      this.balanceRetryTimer = null;
    }
  }

  private applyBalanceLockFromError(error: HttpErrorResponse): void {
    const unlockIsoRaw = typeof error?.error?.balanceDueAt === 'string'
      ? error.error.balanceDueAt
      : null;

    const unlockIso = unlockIsoRaw ?? null;

    if (unlockIso) {
      const parsed = DateTime.fromISO(unlockIso);
      this.balanceRetryUnlockAt = parsed.isValid && parsed.toMillis() > DateTime.now().toMillis()
        ? parsed
        : null;
    } else if (this.booking?.balanceDueAt && this.booking.balanceDueAt.toMillis() > DateTime.now().toMillis()) {
      this.balanceRetryUnlockAt = this.booking.balanceDueAt;
    }

    if (this.balanceRetryUnlockAt) {
      this.balanceRetryErrorMessage = typeof error?.error?.message === 'string'
        ? error.error.message
        : null;
      this.refreshBalanceRetryRemaining();
      this.startBalanceRetryTimer();
    }
  }

  protected readonly BookingStatus = BookingStatus;
}
